import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useRealtimeWorkflows, type WorkflowSummary, type HiddenFilter } from '@/hooks/useRealtimeWorkflows';
import { useLabOS } from '@/lib/labos';
import WorkflowBrowser from '@/components/WorkflowBrowser';
import { Workflow, Plus, Loader2, RefreshCw, Eye, Archive, EyeOff, AlertTriangle } from 'lucide-react';

interface ConfirmArchiveState {
  open: boolean;
  workflowId: string | null;
  workflowName: string;
}

export default function WorkflowsPage() {
  const { workflows, loading, error, refreshWorkflows, hiddenFilter, setHiddenFilter, toggleHideWorkflow, filteringWorkflows } =
    useRealtimeWorkflows();
  const { user } = useLabOS();
  const [refreshing, setRefreshing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<ConfirmArchiveState>({
    open: false,
    workflowId: null,
    workflowName: '',
  });
  const [archiving, setArchiving] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshWorkflows();
    setRefreshing(false);
  };

  const handleToggleArchive = async (id: string) => {
    const workflow = workflows.find((w) => w.id === id);
    if (!workflow) return;

    const isCurrentlyHidden = workflow.hidden === true;
    const isPublished = workflow.status === 'published';

    // Show confirmation drawer for archiving published workflows
    if (!isCurrentlyHidden && isPublished) {
      setConfirmArchive({
        open: true,
        workflowId: id,
        workflowName: workflow.name,
      });
      return;
    }

    // Directly unarchive or archive unpublished workflows
    try {
      await toggleHideWorkflow(id, !isCurrentlyHidden);
    } catch (err) {
      console.error('Failed to toggle archive status:', err);
    }
  };

  const handleConfirmArchive = async () => {
    if (!confirmArchive.workflowId) return;

    setArchiving(true);
    try {
      await toggleHideWorkflow(confirmArchive.workflowId, true);
      setConfirmArchive({ open: false, workflowId: null, workflowName: '' });
    } catch (err) {
      console.error('Failed to archive workflow:', err);
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 overflow-auto p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Workflow className="h-6 w-6" />
              Workflows
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Upload and manage your ComfyUI workflows</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Link to="/admin/workflows/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Workflow
              </Button>
            </Link>
          </div>
        </header>

        <Tabs value={hiddenFilter} onValueChange={(v) => setHiddenFilter(v as HiddenFilter)} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Active
            </TabsTrigger>
            <TabsTrigger value="only" className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Archived
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-6">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {workflows.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              {hiddenFilter === 'only' ? (
                <>
                  <Archive className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No archived workflows</h3>
                  <p className="text-sm text-muted-foreground mb-6">Workflows you archive will appear here</p>
                  <Button variant="outline" onClick={() => setHiddenFilter('active')}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Active Workflows
                  </Button>
                </>
              ) : (
                <>
                  <Workflow className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No workflows yet</h3>
                  <p className="text-sm text-muted-foreground mb-6">Upload a ComfyUI workflow JSON to get started</p>
                  <Link to="/admin/workflows/new">
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Workflow
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {(refreshing || filteringWorkflows) && (
              <div className="absolute inset-0 z-10 flex items-start justify-center pt-16 bg-background/50 backdrop-blur-[1px]">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            <WorkflowBrowser
              workflows={workflows}
              currentUserId={user?.uid}
              managerView
              singleCategory
              defaultScope="mine"
              linkTo={(wf: WorkflowSummary) => `/admin/workflows/${wf.id}`}
              onToggleArchive={handleToggleArchive}
            />
          </div>
        )}

        <Drawer
          open={confirmArchive.open}
          onOpenChange={(open) => !archiving && setConfirmArchive({ open, workflowId: null, workflowName: '' })}
        >
          <DrawerContent>
            <div className="flex flex-col h-full">
              <DrawerHeader className="flex-shrink-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-500/10">
                    <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-500" />
                  </div>
                  <DrawerTitle>Archive Published Workflow?</DrawerTitle>
                </div>
              </DrawerHeader>

              <div className="flex-1 overflow-auto px-6">
                <DrawerDescription className="text-left text-base">
                  You're about to archive <strong className="font-semibold text-foreground">{confirmArchive.workflowName}</strong>
                  , which is currently published.
                </DrawerDescription>

                <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 rounded-lg">
                  <h4 className="text-sm font-semibold text-foreground mb-2">What happens when you archive?</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-500 mt-0.5">•</span>
                      <span>The workflow will be immediately hidden from all users</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-500 mt-0.5">•</span>
                      <span>Users won't be able to run it anymore</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-500 mt-0.5">•</span>
                      <span>You can unarchive it anytime to restore access</span>
                    </li>
                  </ul>
                </div>
              </div>

              <DrawerFooter className="flex-shrink-0 mt-auto">
                <Button onClick={handleConfirmArchive} disabled={archiving} variant="destructive" className="w-full">
                  {archiving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4 mr-2" />
                      Archive Workflow
                    </>
                  )}
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline" disabled={archiving} className="w-full">
                    Cancel
                  </Button>
                </DrawerClose>
              </DrawerFooter>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
}
